import Dexie from 'dexie';
import allSeedRecipes, { familyBabyRecipes } from './seedRecipes';
import { DEFAULT_CATEGORIES } from '../constants/recipeConstants';

export const db = new Dexie('CooksmithApp');

// Seed content version. Bump this whenever a new batch of seed recipes is
// added so existing installs (which skip the count===0 full seed) receive
// the new recipes via the top-up path in seedDatabase() — without touching
// user-created data. History:
//   1 — original 148 recipes
//   2 — family-kitchen batch: +24 recipes (familyBaby.js), audience field,
//       'Family Meals' + 'Baby & Toddler' categories
const SEED_VERSION = 2;

// Titles → audience for the seed catalogue, used by the v3 upgrade so
// recipes already sitting in an existing install's Dexie DB get the same
// audience re-tags as fresh seeds (user-created recipes default to 'adults').
const SEED_AUDIENCE_BY_TITLE = new Map(
  allSeedRecipes.map((r) => [r.title, r.audience || 'adults'])
);

// Version 3: family-kitchen model — every recipe carries an `audience`
// ('adults' | 'family' | 'toddler' | 'baby'), indexed for filtering.
db.version(3).stores({
  recipes: '++id, title, *categories, *tags, *ageGroups, cuisine, region, country, difficulty, favorite, communityRating, createdAt, lastCookedAt, audience',
  mealPlans: '++id, weekStart, &[weekStart+userId]',
  groceryLists: '++id, name, createdAt, active',
  groceryItems: '++id, listId, item, aisle, checked, recipeId',
  categories: '++id, &name, position',
  reviews: '++id, recipeId, rating, createdAt',
  settings: 'key'
}).upgrade(tx => {
  return tx.table('recipes').toCollection().modify(recipe => {
    if (!recipe.audience) {
      recipe.audience = SEED_AUDIENCE_BY_TITLE.get(recipe.title) || 'adults';
    }
  });
});

// Version 2: Enhanced schema with cuisine, nutrition, age groups, community ratings
db.version(2).stores({
  recipes: '++id, title, *categories, *tags, *ageGroups, cuisine, region, country, difficulty, favorite, communityRating, createdAt, lastCookedAt',
  mealPlans: '++id, weekStart, &[weekStart+userId]',
  groceryLists: '++id, name, createdAt, active',
  groceryItems: '++id, listId, item, aisle, checked, recipeId',
  categories: '++id, &name, position',
  reviews: '++id, recipeId, rating, createdAt',
  settings: 'key'
}).upgrade(tx => {
  // Migrate existing recipes to include new fields with defaults
  return tx.table('recipes').toCollection().modify(recipe => {
    if (!recipe.cuisine) recipe.cuisine = '';
    if (!recipe.region) recipe.region = '';
    if (!recipe.country) recipe.country = '';
    if (!recipe.difficulty) recipe.difficulty = 'medium';
    if (!recipe.spiceLevel) recipe.spiceLevel = 0;
    if (!recipe.ageGroups) recipe.ageGroups = ['teen', 'adult'];
    if (!recipe.nutrition) recipe.nutrition = null;
    if (!recipe.images) recipe.images = recipe.image ? [{ url: recipe.image, caption: 'Recipe photo', isPrimary: true }] : [];
    if (!recipe.communityRating) recipe.communityRating = 0;
    if (!recipe.communityRatingCount) recipe.communityRatingCount = 0;
  });
});

// Keep version 1 for fresh installs that might have old data
db.version(1).stores({
  recipes: '++id, title, *categories, *tags, favorite, createdAt, lastCookedAt',
  mealPlans: '++id, weekStart, &[weekStart+userId]',
  groceryLists: '++id, name, createdAt, active',
  groceryItems: '++id, listId, item, aisle, checked, recipeId',
  categories: '++id, &name, position',
  settings: 'key'
});

export const AISLES = [
  'Produce',
  'Dairy & Eggs',
  'Meat & Seafood',
  'Bakery',
  'Frozen',
  'Pantry & Dry Goods',
  'Canned & Jarred',
  'Condiments & Sauces',
  'Spices & Seasonings',
  'Beverages',
  'Snacks',
  'Other'
];

const AISLE_MAP = {
  'lettuce': 'Produce', 'tomato': 'Produce', 'tomatoes': 'Produce', 'onion': 'Produce',
  'onions': 'Produce', 'garlic': 'Produce', 'peppers': 'Produce',
  'carrot': 'Produce', 'carrots': 'Produce', 'potato': 'Produce', 'potatoes': 'Produce',
  'celery': 'Produce', 'mushroom': 'Produce', 'mushrooms': 'Produce', 'spinach': 'Produce',
  'kale': 'Produce', 'broccoli': 'Produce', 'cucumber': 'Produce', 'avocado': 'Produce',
  'lemon': 'Produce', 'lemons': 'Produce', 'lime': 'Produce', 'limes': 'Produce',
  'ginger': 'Produce', 'cilantro': 'Produce', 'parsley': 'Produce', 'basil': 'Produce',
  'scallions': 'Produce', 'green onions': 'Produce', 'zucchini': 'Produce',
  'bell pepper': 'Produce', 'jalapeño': 'Produce', 'corn': 'Produce',
  'apple': 'Produce', 'apples': 'Produce', 'banana': 'Produce', 'bananas': 'Produce',
  'berries': 'Produce', 'strawberries': 'Produce', 'blueberries': 'Produce',
  'cauliflower': 'Produce', 'cabbage': 'Produce', 'eggplant': 'Produce',
  'sweet potato': 'Produce', 'mango': 'Produce', 'pineapple': 'Produce',
  'peas': 'Produce', 'snap peas': 'Produce', 'bean sprouts': 'Produce',
  'bok choy': 'Produce', 'lemongrass': 'Produce', 'galangal': 'Produce',

  'milk': 'Dairy & Eggs', 'butter': 'Dairy & Eggs', 'eggs': 'Dairy & Eggs', 'egg': 'Dairy & Eggs',
  'cheese': 'Dairy & Eggs', 'cream': 'Dairy & Eggs', 'yogurt': 'Dairy & Eggs',
  'sour cream': 'Dairy & Eggs', 'cream cheese': 'Dairy & Eggs', 'parmesan': 'Dairy & Eggs',
  'mozzarella': 'Dairy & Eggs', 'cheddar': 'Dairy & Eggs', 'heavy cream': 'Dairy & Eggs',
  'half and half': 'Dairy & Eggs', 'whipping cream': 'Dairy & Eggs',
  'paneer': 'Dairy & Eggs', 'halloumi': 'Dairy & Eggs', 'feta': 'Dairy & Eggs',
  'ghee': 'Dairy & Eggs', 'buttermilk': 'Dairy & Eggs',

  'chicken': 'Meat & Seafood', 'beef': 'Meat & Seafood', 'pork': 'Meat & Seafood',
  'salmon': 'Meat & Seafood', 'shrimp': 'Meat & Seafood', 'fish': 'Meat & Seafood',
  'ground beef': 'Meat & Seafood', 'ground turkey': 'Meat & Seafood', 'bacon': 'Meat & Seafood',
  'sausage': 'Meat & Seafood', 'steak': 'Meat & Seafood', 'turkey': 'Meat & Seafood',
  'lamb': 'Meat & Seafood', 'tuna': 'Meat & Seafood', 'duck': 'Meat & Seafood',
  'liver': 'Meat & Seafood', 'prawn': 'Meat & Seafood', 'crab': 'Meat & Seafood',
  'anchovy': 'Meat & Seafood', 'calamari': 'Meat & Seafood',

  'bread': 'Bakery', 'tortillas': 'Bakery', 'rolls': 'Bakery', 'baguette': 'Bakery',
  'pita': 'Bakery', 'naan': 'Bakery', 'croissant': 'Bakery',

  'frozen vegetables': 'Frozen', 'ice cream': 'Frozen', 'frozen peas': 'Frozen',
  'frozen berries': 'Frozen', 'frozen corn': 'Frozen',

  'flour': 'Pantry & Dry Goods', 'sugar': 'Pantry & Dry Goods', 'rice': 'Pantry & Dry Goods',
  'pasta': 'Pantry & Dry Goods', 'spaghetti': 'Pantry & Dry Goods', 'penne': 'Pantry & Dry Goods',
  'noodles': 'Pantry & Dry Goods', 'oats': 'Pantry & Dry Goods', 'quinoa': 'Pantry & Dry Goods',
  'lentils': 'Pantry & Dry Goods', 'breadcrumbs': 'Pantry & Dry Goods',
  'panko': 'Pantry & Dry Goods', 'brown sugar': 'Pantry & Dry Goods',
  'powdered sugar': 'Pantry & Dry Goods', 'baking soda': 'Pantry & Dry Goods',
  'baking powder': 'Pantry & Dry Goods', 'cornstarch': 'Pantry & Dry Goods',
  'vanilla extract': 'Pantry & Dry Goods', 'chocolate chips': 'Pantry & Dry Goods',
  'cocoa powder': 'Pantry & Dry Goods', 'nuts': 'Pantry & Dry Goods',
  'almonds': 'Pantry & Dry Goods', 'walnuts': 'Pantry & Dry Goods',
  'chia seeds': 'Pantry & Dry Goods', 'sesame seeds': 'Pantry & Dry Goods',
  'couscous': 'Pantry & Dry Goods', 'basmati rice': 'Pantry & Dry Goods',
  'jasmine rice': 'Pantry & Dry Goods', 'arborio rice': 'Pantry & Dry Goods',
  'ramen noodles': 'Pantry & Dry Goods', 'rice noodles': 'Pantry & Dry Goods',
  'dried beans': 'Pantry & Dry Goods', 'chickpea flour': 'Pantry & Dry Goods',

  'tomato sauce': 'Canned & Jarred', 'diced tomatoes': 'Canned & Jarred',
  'crushed tomatoes': 'Canned & Jarred', 'tomato paste': 'Canned & Jarred',
  'beans': 'Canned & Jarred', 'black beans': 'Canned & Jarred',
  'chickpeas': 'Canned & Jarred', 'coconut milk': 'Canned & Jarred',
  'broth': 'Canned & Jarred', 'chicken broth': 'Canned & Jarred',
  'vegetable broth': 'Canned & Jarred', 'stock': 'Canned & Jarred',
  'coconut cream': 'Canned & Jarred', 'tahini': 'Canned & Jarred',
  'curry paste': 'Canned & Jarred', 'chipotle': 'Canned & Jarred',

  'soy sauce': 'Condiments & Sauces', 'olive oil': 'Condiments & Sauces',
  'vegetable oil': 'Condiments & Sauces', 'vinegar': 'Condiments & Sauces',
  'hot sauce': 'Condiments & Sauces', 'ketchup': 'Condiments & Sauces',
  'mustard': 'Condiments & Sauces', 'mayonnaise': 'Condiments & Sauces',
  'honey': 'Condiments & Sauces', 'maple syrup': 'Condiments & Sauces',
  'worcestershire': 'Condiments & Sauces', 'sesame oil': 'Condiments & Sauces',
  'fish sauce': 'Condiments & Sauces', 'sriracha': 'Condiments & Sauces',
  'mirin': 'Condiments & Sauces', 'rice vinegar': 'Condiments & Sauces',
  'oyster sauce': 'Condiments & Sauces', 'hoisin sauce': 'Condiments & Sauces',
  'gochujang': 'Condiments & Sauces', 'miso paste': 'Condiments & Sauces',
  'tamarind': 'Condiments & Sauces', 'harissa': 'Condiments & Sauces',

  'salt': 'Spices & Seasonings', 'pepper': 'Spices & Seasonings',
  'cumin': 'Spices & Seasonings', 'paprika': 'Spices & Seasonings',
  'oregano': 'Spices & Seasonings', 'thyme': 'Spices & Seasonings',
  'rosemary': 'Spices & Seasonings', 'cinnamon': 'Spices & Seasonings',
  'chili powder': 'Spices & Seasonings', 'garlic powder': 'Spices & Seasonings',
  'onion powder': 'Spices & Seasonings', 'cayenne': 'Spices & Seasonings',
  'turmeric': 'Spices & Seasonings', 'nutmeg': 'Spices & Seasonings',
  'bay leaves': 'Spices & Seasonings', 'red pepper flakes': 'Spices & Seasonings',
  'italian seasoning': 'Spices & Seasonings', 'garam masala': 'Spices & Seasonings',
  'coriander': 'Spices & Seasonings', 'cardamom': 'Spices & Seasonings',
  'saffron': 'Spices & Seasonings', 'star anise': 'Spices & Seasonings',
  'five spice': 'Spices & Seasonings', 'smoked paprika': 'Spices & Seasonings',
  'sumac': 'Spices & Seasonings', 'za\'atar': 'Spices & Seasonings',
  'berbere': 'Spices & Seasonings', 'allspice': 'Spices & Seasonings',
  'cloves': 'Spices & Seasonings', 'fenugreek': 'Spices & Seasonings',
};

export function guessAisle(ingredientName) {
  const lower = ingredientName.toLowerCase().trim();
  for (const [keyword, aisle] of Object.entries(AISLE_MAP)) {
    if (lower.includes(keyword)) return aisle;
  }
  return 'Other';
}

// Normalise a seed recipe for insertion: timestamps + guaranteed audience.
function prepareSeedRecipe(recipe) {
  return {
    ...recipe,
    audience: recipe.audience || 'adults',
    createdAt: recipe.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Top-up path for existing installs: adds only NEW seed content (recipes by
// title, categories by name) introduced since the install was first seeded.
// Never modifies or re-adds anything the user may have edited or deleted
// beyond the new batch itself.
async function topUpSeeds() {
  const stored = await db.settings.get('seedVersion');
  const installedSeedVersion = stored?.value || 1;
  if (installedSeedVersion >= SEED_VERSION) return;

  if (installedSeedVersion < 2) {
    // Add the new family-kitchen categories if they are not already present
    const existingCategoryNames = new Set(await db.categories.orderBy('name').keys());
    const newCategories = DEFAULT_CATEGORIES.filter(c => !existingCategoryNames.has(c.name));
    if (newCategories.length) await db.categories.bulkAdd(newCategories);

    // Add only the seed-version-2 recipe batch, skipping any title the user
    // already has (e.g. they created a recipe with the same name).
    const existingTitles = new Set(await db.recipes.orderBy('title').keys());
    const newRecipes = familyBabyRecipes
      .filter(r => !existingTitles.has(r.title))
      .map(prepareSeedRecipe);
    if (newRecipes.length) await db.recipes.bulkAdd(newRecipes);
  }

  await db.settings.put({ key: 'seedVersion', value: SEED_VERSION });
}

export async function seedDatabase() {
  const recipeCount = await db.recipes.count();
  if (recipeCount > 0) {
    // Existing install: deliver any newly shipped seed content, then bail.
    await topUpSeeds();
    return;
  }

  await db.categories.bulkAdd(DEFAULT_CATEGORIES);

  // Add timestamp + audience defaults to all seed recipes
  const recipesWithTimestamps = allSeedRecipes.map(prepareSeedRecipe);

  await db.recipes.bulkAdd(recipesWithTimestamps);

  // Set up initial meal plan for current week
  const today = new Date();
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  monday.setHours(0, 0, 0, 0);
  const weekStart = monday.toISOString().split('T')[0];

  await db.mealPlans.add({
    weekStart,
    days: {
      monday: { dinner: [1] },
      tuesday: { dinner: [2] },
      wednesday: { dinner: [4] },
      thursday: { dinner: [] },
      friday: { dinner: [6] },
      saturday: { lunch: [5], dinner: [] },
      sunday: { breakfast: [3], dinner: [] },
    },
  });

  await db.groceryLists.add({
    name: 'This Week',
    createdAt: new Date().toISOString(),
    active: 1,
  });

  // Initialize default settings
  await db.settings.put({ key: 'unitSystem', value: 'imperial' });
  await db.settings.put({ key: 'seedVersion', value: SEED_VERSION });
}
